/**
 * Dynamic option transforms — evaluated at runtime to modify step config
 * based on previously captured answers.
 *
 * Four transform types:
 * - `filter_options`: Show/hide options based on answer-driven conditions
 * - `swap_assets`: Replace option fields (images, etc.) based on a variant key
 * - `template_url`: Interpolate `{{answerKey}}` placeholders in option URLs
 * - `template_fields`: Interpolate `{{answerKey}}` placeholders in top-level step data fields
 *
 * NX-9279
 */
import { evaluateRules, type RuleConditionGroup, type RuleFacts } from "@nextday/rules";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FilterOptionsTransform = {
  readonly type: "filter_options";
  /** Config field containing the options array (default: "options"). */
  readonly target: string;
  readonly rules: ReadonlyArray<{
    /** Option IDs this visibility rule applies to. */
    readonly option_ids: ReadonlyArray<string>;
    /** json-rules-engine condition — option shown only when this passes. */
    readonly visible_when: RuleConditionGroup;
  }>;
};

export type SwapAssetsTransform = {
  readonly type: "swap_assets";
  /** Config field containing the options array. */
  readonly target: string;
  /** Answer key whose value determines which variant to use. */
  readonly variant_key: string;
  /** Map of answer value → (option id → field overrides). */
  readonly variants: Readonly<Record<string, Readonly<Record<string, Record<string, unknown>>>>>;
};

export type TemplateUrlTransform = {
  readonly type: "template_url";
  /** Config field containing the options array. */
  readonly target: string;
  /** Option fields to interpolate (e.g. ["imageUrl"]). */
  readonly fields: ReadonlyArray<string>;
};

export type TemplateFieldsTransform = {
  readonly type: "template_fields";
  /** Top-level data fields to interpolate (e.g. ["characterName", "characterImageUrl"]). */
  readonly fields: ReadonlyArray<string>;
};

export type Transform =
  | FilterOptionsTransform
  | SwapAssetsTransform
  | TemplateUrlTransform
  | TemplateFieldsTransform;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type OptionRecord = Record<string, unknown> & { id: string };

const getOptionsArray = (
  config: Record<string, unknown>,
  target: string,
): OptionRecord[] | null => {
  const raw = config[target];
  if (!Array.isArray(raw)) return null;
  return raw as OptionRecord[];
};

const interpolate = (template: string, answers: Record<string, unknown>): string =>
  template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const val = answers[key];
    if (typeof val === "string") return val;
    if (val === null || val === undefined) return "";
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    return "";
  });

// ---------------------------------------------------------------------------
// Individual transform applicators
// ---------------------------------------------------------------------------

const applyFilterOptions = async (
  config: Record<string, unknown>,
  transform: FilterOptionsTransform,
  facts: RuleFacts,
): Promise<Record<string, unknown>> => {
  const options = getOptionsArray(config, transform.target);
  if (options === null) return config;

  // Build a set of option IDs that should be visible.
  const visibleIds = new Set<string>();
  // Options not covered by any rule are always visible.
  const coveredIds = new Set<string>();

  for (const rule of transform.rules) {
    for (const id of rule.option_ids) {
      coveredIds.add(id);
    }

    // Wrap the condition in a minimal rule for evaluation.
    const result = await evaluateRules(
      [
        {
          id: "filter_check",
          name: "filter_check",
          conditions: rule.visible_when,
          event: { type: "visible" },
          priority: 1,
        },
      ],
      facts,
    );

    if (result.events.length > 0) {
      for (const id of rule.option_ids) {
        visibleIds.add(id);
      }
    }
  }

  const filtered = options.filter((opt) => !coveredIds.has(opt.id) || visibleIds.has(opt.id));

  return { ...config, [transform.target]: filtered };
};

const applySwapAssets = (
  config: Record<string, unknown>,
  transform: SwapAssetsTransform,
  answers: Record<string, unknown>,
): Record<string, unknown> => {
  const options = getOptionsArray(config, transform.target);
  if (options === null) return config;

  const answerValue = answers[transform.variant_key];
  if (typeof answerValue !== "string") return config;

  const variantMap = transform.variants[answerValue];
  if (variantMap === undefined) return config;

  const swapped = options.map((opt) => {
    const overrides = variantMap[opt.id];
    if (overrides === undefined) return opt;
    return { ...opt, ...overrides };
  });

  return { ...config, [transform.target]: swapped };
};

const applyTemplateUrl = (
  config: Record<string, unknown>,
  transform: TemplateUrlTransform,
  answers: Record<string, unknown>,
): Record<string, unknown> => {
  const options = getOptionsArray(config, transform.target);
  if (options === null) return config;

  const interpolated = options.map((opt) => {
    const patched = { ...opt };
    for (const field of transform.fields) {
      const val = patched[field];
      if (typeof val === "string" && val.includes("{{")) {
        patched[field] = interpolate(val, answers);
      }
    }
    return patched;
  });

  return { ...config, [transform.target]: interpolated };
};

const applyTemplateFields = (
  config: Record<string, unknown>,
  transform: TemplateFieldsTransform,
  answers: Record<string, unknown>,
): Record<string, unknown> => {
  const patched = { ...config };
  for (const field of transform.fields) {
    const val = patched[field];
    if (typeof val === "string" && val.includes("{{")) {
      patched[field] = interpolate(val, answers);
    }
    // Support nested objects (e.g. tiles array with imageUrl fields)
    if (Array.isArray(val)) {
      patched[field] = val.map((item: unknown) => {
        if (typeof item === "string" && item.includes("{{")) return interpolate(item, answers);
        if (typeof item !== "object" || item === null) return item;
        const patchedItem = { ...item } as Record<string, unknown>;
        for (const [k, v] of Object.entries(patchedItem)) {
          if (typeof v === "string" && v.includes("{{")) {
            patchedItem[k] = interpolate(v, answers);
          }
        }
        return patchedItem;
      });
    }
  }
  return patched;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply only synchronous transforms (swap_assets, template_url).
 * Can be called during render without deferring to useEffect.
 */
export const applySyncTransforms = (
  config: Record<string, unknown>,
  transforms: ReadonlyArray<Transform>,
  answers: Record<string, unknown>,
): Record<string, unknown> => {
  const { transforms: _strip, ...rest } = config;
  let result: Record<string, unknown> = { ...rest };

  for (const transform of transforms) {
    switch (transform.type) {
      case "swap_assets":
        result = applySwapAssets(result, transform, answers);
        break;
      case "template_url":
        result = applyTemplateUrl(result, transform, answers);
        break;
      case "template_fields":
        result = applyTemplateFields(result, transform, answers);
        break;
      default:
        break;
    }
  }

  return result;
};

export const applyTransforms = async (
  config: Record<string, unknown>,
  transforms: ReadonlyArray<Transform>,
  answers: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  // Strip `transforms` from output so strict Zod schemas don't reject it.
  const { transforms: _strip, ...rest } = config;
  let result: Record<string, unknown> = { ...rest };
  const facts: RuleFacts = { answers };

  for (const transform of transforms) {
    switch (transform.type) {
      case "filter_options":
        result = await applyFilterOptions(result, transform, facts);
        break;
      case "swap_assets":
        result = applySwapAssets(result, transform, answers);
        break;
      case "template_url":
        result = applyTemplateUrl(result, transform, answers);
        break;
      case "template_fields":
        result = applyTemplateFields(result, transform, answers);
        break;
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// Auto-interpolation — catch-all for {{}} tokens without explicit transforms
// ---------------------------------------------------------------------------

const TEMPLATE_TOKEN_RE = /\{\{\w+\}\}/;

/** Recursively interpolate `{{key}}` tokens in all string values. */
export const deepInterpolate = (value: unknown, answers: Record<string, unknown>): unknown => {
  if (typeof value === "string") {
    return TEMPLATE_TOKEN_RE.test(value) ? interpolate(value, answers) : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepInterpolate(item, answers));
  }
  if (typeof value === "object" && value !== null) {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = deepInterpolate(v, answers);
    }
    return result;
  }
  return value;
};

/**
 * Parse transforms from a config object. Returns empty array if none present.
 */
export const parseTransforms = (config: Record<string, unknown>): Transform[] => {
  const raw = config["transforms"];
  if (!Array.isArray(raw)) return [];
  return raw.filter((t): t is Transform => {
    if (typeof t !== "object" || t === null || !("type" in t)) return false;
    const typed = t as Record<string, unknown>;
    switch (typed["type"]) {
      case "filter_options":
        return typeof typed["target"] === "string" && Array.isArray(typed["rules"]);
      case "swap_assets":
        return (
          typeof typed["target"] === "string" &&
          typeof typed["variant_key"] === "string" &&
          typeof typed["variants"] === "object" &&
          typed["variants"] !== null
        );
      case "template_url":
        return typeof typed["target"] === "string" && Array.isArray(typed["fields"]);
      case "template_fields":
        return Array.isArray(typed["fields"]);
      default:
        return false;
    }
  });
};
